import { useCallback, useEffect, useRef, useState } from "react";
import axios from "axios";
import config from "../../../Helpers/config.json";
import { errorMessages } from "../../../Helpers/ErrorMessages";
import { titleCase } from "../../../Utils/titleCaseFunction";

export const useManageMedicine = (actions) => {
  const storage = JSON.parse(sessionStorage.getItem("total_count"));
  const item_count = storage ? storage : 0;
  const count = () => +item_count || 0;

  const [openDialog, setOpenDialog] = useState(false);
  const [product_id, setProductId] = useState("");
  const [product_count, setProductCount] = useState(count);
  const [totalPages, setTotalPages] = useState(1); // New: from backend
  const [activePage, setActivePage] = useState(1);
  const [products, setProducts] = useState([]);
  const [loading, setLoading] = useState("idle");
  const [searchValue, setSearchValue] = useState("");
  const [product_categories, setProductCategories] = useState(null);
  const [categoryList, setCategories] = useState([]);
  const csvLink = useRef();
  const componentRef = useRef();

  // Main API call with per_page=1000
  const retrieveProducts = useCallback(() => {
    setLoading("loading");

    const cat_id = product_categories ? product_categories.category_id ?? "" : "";
    const search = searchValue.trim();

    axios
      .get(`${config.epharmUrl}/Cproduct/products`, {
        params: {
          page_number: activePage,
          category_id: cat_id,
          search: search,
          per_page: 1000, // This is the key change
        },
      })
      .then((response) => {
        const res = response.data;
        const data = res?.data ?? {};
        const arr = data.products ?? [];
        const total_records = data.total_records ?? 0;
        const total_pages_from_backend = data.total_pages ?? 1; // New field from backend

        // Save total count only on first page
        if (activePage === 1) {
          sessionStorage.setItem("total_count", JSON.stringify(total_records));
          setProductCount(total_records);
        }

        setProducts(arr);
        setTotalPages(total_pages_from_backend); // Use backend's total_pages
        setLoading("success");
      })
      .catch((error) => {
        errorMessages(error, null, actions);
        setLoading("error");
      });
  }, [activePage, product_categories, searchValue, actions]);

  // Trigger fetch whenever page, category, or search changes
  useEffect(() => {
    retrieveProducts();
  }, [retrieveProducts]);

  // Fetch categories once
  useEffect(() => {
    axios
      .get(`${config.epharmUrl}/Ccategory/manage_category`)
      .then((res) => {
        const data = res.data;
        const dt = data ? data : {};
        const categories = dt.category_list ? dt.category_list : [];
        const arr = categories.map((category) => ({
          value: category.category_id,
          label: titleCase(category.category_name),
        }));
        setCategories(categories);
      })
      .catch((error) => {
        errorMessages(error, null, actions);
      });
  }, [actions]);

  // Search input handler
  const handleChange = (event) => {
    const value = event.target.value;
    setSearchValue(value);
    setActivePage(1); // Always reset to page 1 when searching
  };

  const handleOpenDialog = (_id) => {
    setProductId(_id);
    setOpenDialog(true);
  };

  const handleCloseDialog = () => {
    setOpenDialog(false);
  };

  const handleDelete = () => {
    axios
      .post(`${config.epharmUrl}/Cproduct/product_delete/${product_id}`)
      .then(() => {
        const arr = products.filter((medicine) => medicine.product_id !== product_id);
        setProducts(arr);
        setProductCount((prev) => prev - 1);
        actions.snackbarActions.successSnackbar("Product deleted successfully");
        setOpenDialog(false);
      })
      .catch((error) => {
        errorMessages(error, null, actions);
        setOpenDialog(false);
      });
  };

  const handlePageChange = (pageNumber) => {
    setActivePage(pageNumber);
  };

  const handleCloseBar = () => {
    actions.snackbarActions.hideSnackBar();
  };

  const handleClickProductCategories = (value) => {
    setProductCategories(value);
    setActivePage(1); // Reset page when changing category
  };

  const isLoading = loading === "loading";
  const isSuccess = loading === "success";
  const isError = loading === "error";

  return {
    openDialog,
    product_id,
    product_count,
    totalPages, // Export this if you want to use it in pagination
    activePage,
    products,
    loading,
    searchValue,
    csvLink,
    handleChange,
    handleOpenDialog,
    handleCloseDialog,
    handleCloseBar,
    handlePageChange,
    handleDelete,
    isLoading,
    isSuccess,
    isError,
    product_categories,
    handleClickProductCategories,
    categoryList,
  };
};